import { useRef, useState, useEffect, useReducer } from 'react'
import GetAppIcon from '@mui/icons-material/GetApp'
import {
  ClickAwayListener,
  Fab,
  Grow,
  MenuItem,
  Popper,
  Paper,
  Tooltip,
  MenuList,
  CircularProgress,
} from '@mui/material'
import { motion } from 'framer-motion'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { useExportDatasetData } from '../../../../hooks/useExportDatasetData'
import { DownloadOption } from '../types'
import { reducer, initialState, ActionType } from './reducer'
import * as css from './styles'
import { RootState } from '@redux/store'
import { t } from '@lingui/core/macro'

interface Props {
  downloadOptions: DownloadOption[]
  loading: boolean
  isPendingRollup: boolean
  download: (id: number | string, type: string) => void
}

// 'Default' value for when downloading from pending
const CSV_DEFAULT_TYPE = 'CSV'

/**
 * The download submission button in the read grid header
 *
 * @param props React props
 * @param {DownloadOption[]} props.downloadOptions The downloadable file formats
 * @param {boolean} props.loading Used to determine if the file formats are loaded
 * @returns
 */
interface DownloadDatasetRouterParams {
  id: string
}

export const DownloadDataset = ({ downloadOptions, loading, isPendingRollup, download }: Props) => {
  const [tooltipOpen, setTooltipOpen] = useState<boolean>(false)
  const [menuOpen, setMenuOpen] = useState<boolean>(false)
  const [state, dispatch] = useReducer(reducer, initialState)
  const anchorRef = useRef<HTMLButtonElement | null>(null)
  const title = loading
    ? t`Loading desintation formats`
    : t`Download a copy of this submission in any destination format`

  const params = useParams<DownloadDatasetRouterParams>()
  const template = useSelector(
    (state: RootState) => state.Submissions.templatesById[Number(params.id)],
  )
  const { refetch } = useExportDatasetData(
    true,
    Number(state.fileFormatId),
    state.fileType,
    state.fileName,
  )

  /**
   * Due to the way that useExportDatasetData is structured, there was no way to call it directly
   * from the button click, so we need to trigger a state change in order to update the hook values.
   */
  useEffect(() => {
    const { fileType, fileFormatId } = state
    if (fileType !== '') {
      void downloadFile(fileFormatId, fileType)
    }
  }, [state.fileType, state.fileName, state.fileFormatId])

  return (
    <div>
      <Tooltip
        title={title}
        open={tooltipOpen && !menuOpen}
        onClose={handleCloseToolip}
        onOpen={handleOpenTooltip}
        data-testid="download-dataset-tooltip"
      >
        <Fab
          color="secondary"
          ref={anchorRef}
          size="medium"
          css={[css.fab, (downloadOptions.length === 0 || loading) && css.disabled]}
          onClick={handleToggleMenu}
          id="composition-button"
          aria-controls={menuOpen ? 'composition-menu' : undefined}
          aria-expanded={menuOpen ? 'true' : undefined}
          aria-haspopup="true"
          data-testid="download-dataset-fab"
          component={motion.button}
          initial={{
            opacity: 0,
          }}
          animate={{
            opacity: 1,
          }}
          exit={{
            opacity: 0,
          }}
        >
          {loading ? (
            <CircularProgress size={15} data-testid="circular-progress" />
          ) : (
            <GetAppIcon data-testid="get-app-icon" />
          )}
        </Fab>
      </Tooltip>
      <Popper
        css={css.downloadMenu}
        open={menuOpen}
        anchorEl={anchorRef.current}
        role={undefined}
        placement="bottom-end"
        data-testid="download-dataset-popper"
        transition
        disablePortal
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            data-testid="grow"
            style={{
              transformOrigin: placement === 'bottom-end' ? 'left top' : 'left bottom',
            }}
          >
            <Paper data-testid="download-paper">
              <ClickAwayListener onClickAway={handleCloseMenu} data-testid="click-away-listener">
                <MenuList
                  autoFocusItem={menuOpen}
                  id="composition-menu"
                  aria-labelledby="composition-button"
                  data-testid="download-dataset-menu-list"
                >
                  {downloadOptions.map(down => (
                    <MenuItem
                      key={down.id}
                      onClick={() => {
                        updateDownloadOptions(down)
                      }}
                      data-testid="download-dataset-menu-item"
                    >
                      {down.name}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </div>
  )

  function handleOpenTooltip(): void {
    setTooltipOpen(true)
  }

  function handleCloseToolip(): void {
    setTooltipOpen(false)
  }

  function handleToggleMenu(): void {
    setMenuOpen(prevState => {
      if (prevState) {
        setTooltipOpen(true)
        return !prevState
      }

      setTooltipOpen(false)
      return !prevState
    })
  }

  function handleCloseMenu(): void {
    setMenuOpen(false)
  }

  /**
   * This triggers the necessary state update.
   * @param selectedOption
   */
  function updateDownloadOptions(selectedOption: DownloadOption) {
    const { fileName, fileType, fileFormatId } = extractOptions(selectedOption)

    dispatch({
      type: ActionType.setAll,
      payload: {
        fileName,
        fileType,
        fileFormatId,
      },
    })
  }

  function extractOptions(selectedOption: DownloadOption): {
    fileName: string
    fileType: string
    fileFormatId: string
  } {
    const fileName = template ? template.name : 'download'

    // Since Aimia only supports CSV or XLSX and requires the file type id, 'default' will be set to CSV.
    if (isPendingRollup) {
      if (selectedOption.id === 'default') {
        return {
          fileName,
          fileType: CSV_DEFAULT_TYPE,
          fileFormatId: '',
        }
      }
      return {
        fileName,
        fileFormatId: selectedOption.exportConfigurationId || selectedOption.id,
        fileType: selectedOption.type?.toUpperCase(),
      }
    }

    // Normal submission download (not from pending rollup)
    const fileType = selectedOption.id === 'default' ? 'default' : selectedOption.type
    return {
      fileName: selectedOption.name,
      fileFormatId: selectedOption.id,
      fileType,
    }
  }

  /**
   * Downloads the file. In the case of a pending rollup, we need to make the request to Aimia, which
   * supports SCD.
   * @param id
   * @param type
   */
  async function downloadFile(id, type) {
    if (isPendingRollup) {
      await refetch()
    } else {
      download(id, type)
    }
    setMenuOpen(false)
    dispatch({
      type: ActionType.reset,
    })
  }
}
